import { Badge, Group, Text } from "@mantine/core";
import { IconSend2 } from "@tabler/icons-react";
import {
  TableActions,
  TableAction,
  EditAction,
  DeleteAction,
  actionsColumn,
} from "../TableActions";
import dayjs from "dayjs";
import { DataTable } from "../DataTable";
import { useDeleteAnnouncement } from "../../api/announcements";
import { useDeleteConfirm } from "../../hooks/useDeleteConfirm";
import openSendAnnouncementNotificationsModal from "./SendAnnouncementNotificationsModal";

const AnnouncementsTable = ({
  data,
  pagination,
  tagColors,
  hideTextField = false,
  deleteModalTitle = "announcement",
}) => {
  const { mutate } = useDeleteAnnouncement();
  const confirmDelete = useDeleteConfirm(deleteModalTitle, mutate);

  const tooltipText = (row) => {
    if (row.notifiedAt) return "Notification has been already sent";
    if (
      row.shouldAutoNotify &&
      row.scheduledAt &&
      dayjs(row.scheduledAt).isAfter(dayjs())
    )
      return "Push notification for this Announcement will be sent out at the scheduled time.";
    return "Send notification";
  };

  const sendNotificationDisabled = (row) => {
    if (row.notifiedAt) return true;
    if (
      row.shouldAutoNotify &&
      row.scheduledAt &&
      dayjs(row.scheduledAt).isAfter(dayjs())
    )
      return true;
    return false;
  };

  return (
    <DataTable
      records={data?.data}
      columns={[
        {
          accessor: "title",
          render: (row) => (
            <Group>
              <Text size="sm">{row.title}</Text>
              {row.isVisible && (
                <Badge variant="dot" color="green">
                  Visible
                </Badge>
              )}
            </Group>
          ),
        },
        { accessor: "text", hidden: hideTextField },
        {
          accessor: "targetedTags",
          title: "Targeted tags",
          render: (row) => {
            if (!row.targetedTags?.length) return "All ticket holders";

            return (
              <Group gap="xs">
                {row.targetedTags.map((tag) => (
                  <Badge
                    key={tag.id}
                    color={
                      tagColors?.find(
                        (tagColor) => tagColor.subtype === tag.subtype
                      )?.color || "blue"
                    }
                    variant="light"
                  >
                    {tag.title}
                  </Badge>
                ))}
              </Group>
            );
          },
          width: 150,
        },
        {
          accessor: "scheduledAt",
          title: "Scheduled at",
          render: (row) => {
            if (!row.scheduledAt) return "-";
            const isFuture = dayjs(row.scheduledAt).isAfter(dayjs());
            return (
              <Group gap="xs">
                <Text size="sm">
                  {dayjs(row.scheduledAt).format("DD/MM/YYYY HH:mm")}
                </Text>
                {isFuture && (
                  <Badge variant="light" color="violet" size="sm">
                    Scheduled
                  </Badge>
                )}
              </Group>
            );
          },
          width: 200,
        },
        {
          accessor: "type",
          render: (row) => (
            <>
              {row.type === "info" && (
                <Badge variant="light" color="blue">
                  Info
                </Badge>
              )}
              {row.type === "warning" && (
                <Badge variant="light" color="orange">
                  Warning
                </Badge>
              )}
              {row.type === "special" && (
                <Badge variant="light" color="purple">
                  Special
                </Badge>
              )}
            </>
          ),
          width: 150,
        },
        actionsColumn((row) => (
          <TableActions>
            <TableAction
              label={tooltipText(row)}
              icon={IconSend2}
              color="green"
              onClick={() => openSendAnnouncementNotificationsModal(row.id)}
              disabled={sendNotificationDisabled(row)}
            />
            <EditAction to={`/app/announcements/${row.id}`} />
            <DeleteAction onDelete={() => confirmDelete({ id: row.id })} />
          </TableActions>
        )),
      ]}
      page={pagination.data.page}
      onPageChange={(page) =>
        pagination.setPagination({ ...pagination.data, page })
      }
      recordsPerPage={data?.pagination?.perPage}
      totalRecords={data?.pagination?.total}
    />
  );
};

export default AnnouncementsTable;
